import Cookie from 'js-cookie';
import React, { Fragment } from 'react';
import { bindActionCreators } from 'redux';
import LeagueRiverComponent from '~/components/ArticleLeagueRiver/ArticleLeagueRiver';
import I18nLeagueRiver from '~/components/I18nLeagueRiver/I18nLeagueRiver';
import Layout from '~/components/Layout/Layout';
import MetaTags from '~/components/MetaTags/MetaTags';
import NotFound from '~/components/NotFound/NotFound';
import TranslationNotice from '~/components/TranslationNotice/TranslationNotice';
import TranslationRateBar from '~/components/TranslationRateBar/TranslationRateBar';
import ArticleComponent from '~/containers/ArticleContainer/ArticleContainer';
import withState from '~/hocs/withState';
import { operations } from '~/state/pageData';
import { getTicker } from '~/state/ScoreTicker/ScoreTicker';
import { initStore } from '~/state/store';
import { operations as uaOperations } from '~/state/userAgent';
import logEvent from '~/util/analytics';
import { getArticleCard } from '~/util/getContent';
import getOr from '~/util/getOr';
import isBixbyUser from '~/util/isBixbyUser';

const setBetaCookie = () => {
  if (!Cookie.get('beta')) {
    Cookie.set('beta', true, {
      path: '/',
      expires: 30,
      domain: 'thescore.com',
    });
  }
};

// Finds all anchor links in the content, filters out anchor links with "/players/", and then replaces it with the content inside of it.
const parseOutPlayerLinksFromArticle = (contentToParse: string, leagueSlug: string) => {
  let contents = contentToParse;
  const anchorWithoutLinkComHrefPattern = /<a (?!(?:href=["'](http:\/\/)?link\.com\/?.*?["']))[^>]*>(.*?)<\/a>/gm;
  contents = contents.replace(anchorWithoutLinkComHrefPattern, (link: string) => {
    if (link.includes(`${leagueSlug}/players/`)) {
      return link.replace(/<.*?>/g, '');
    }

    return link;
  });

  return contents;
};

interface ArticleProps {
  intl: Intl;
  article: Article;
  riverData: River;
  userIdentifier: string;
  experiments?: any[];
  league: League;
  getTicker: (...args: any[]) => any;
  url: object;
  utmSource?: string;
  bixbyUser?: boolean;
  leagueSlug: string;
}

class ArticlePage extends React.Component<ArticleProps, {}> {
  static defaultProps = {
    experiments: [],
    utmSource: null,
    leagueSlug: 'top_news',
  };

  static displayName = 'ArticlePage';

  static async getInitialProps(props) {
    let statusCode;
    const bixbyUser = props.isServer ? isBixbyUser(props.req.headers) : false;
    const articleId = parseInt(props.query.articleId, 10);
    const storedArticles = getOr(props.store.getState().pageData, 'riverData.relatedContent', null);
    const json = await getArticleCard(articleId, storedArticles, props.locale);
    const leagueSlug = /wolym/g.test(json.article.web_league_slug)
      ? 'wolym'
      : json.article.web_league_slug;

    // Override withPageData settings to use content card league info
    if (!props.store.getState().menuData.data) await props.store.dispatch(operations.setMenu());

    props.store.dispatch(operations.setAdConfig(json.article, 'article'));
    props.store.dispatch(uaOperations.storeBixbyFlag(bixbyUser));

    const league = props.store.getState().pageData.currentLeague;

    await props.store.dispatch(
      operations.loadLeagueRiver({
        league,
        currentCardId: null,
        before: null,
        riverData: { relatedContent: [], newBefore: null },
      })
    );

    if (!json.article.id) {
      // eslint-disable-next-line
      if (props.res) props.res.statusCode = 404;
      else statusCode = 404;
    }

    return {
      leagueSlug,
      league,
      statusCode,
      bixbyUser,
      article: json.article,
    };
  }

  componentDidMount() {
    logEvent('page_view', {
      league: this.props.article.web_league_slug,
      content_card_type: 'article',
      page_type: 'article',
      article_id: this.props.article.id,
      channel: this.props.bixbyUser ? 'bixby' : 'web',
      page_url: getOr(this.props, 'url.asPath', ''),
    });
    setBetaCookie();
    this.props.getTicker(this.props.league.slug);
  }

  componentDidUpdate(oldProps) {
    if (getOr(this.props, 'url.asPath', '') !== getOr(oldProps, 'url.asPath', '')) {
      logEvent('page_view', {
        league: this.props.article.web_league_slug,
        content_card_type: 'article',
        page_type: 'article',
        article_id: this.props.article.id,
        channel: this.props.bixbyUser ? 'bixby' : 'web',
        page_url: getOr(this.props, 'url.asPath', ''),
      });
    }
  }

  renderArticle() {
    const { intl: { locale }, url, article, riverData, league, leagueSlug } = this.props;

    const articleToPass = {
      ...article,
      content: parseOutPlayerLinksFromArticle(article.content, leagueSlug),
    };

    if (locale !== 'en') {
      return (
        <Fragment>
          <TranslationNotice resourceUri={getOr(url, 'asPath', '')} />
          <TranslationRateBar />
          <I18nLeagueRiver league={league}>
            <ArticleComponent article={articleToPass} currentCard isLarge />
          </I18nLeagueRiver>
        </Fragment>
      );
    }

    return (
      <LeagueRiverComponent league={league} riverData={riverData} currentCardId={articleToPass.id}>
        <ArticleComponent article={articleToPass} currentCard isLarge />
      </LeagueRiverComponent>
    );
  }

  render() {
    const { article, leagueSlug } = this.props;

    if (!article.id) {
      return <NotFound leagueSlug={leagueSlug} />;
    }
    return (
      <Layout>
        <MetaTags type="article" content={article} />
        {this.renderArticle()}
      </Layout>
    );
  }
}

const mapStateToProps = state => ({
  riverData: state.pageData.riverData,
  utmSource: state.utmSource.data,
});

const mapDispatchToProps = dispatch => ({
  getTicker: bindActionCreators(getTicker, dispatch),
});

export default withState({
  mapStateToProps,
  mapDispatchToProps,
  createStore: initStore,
})(ArticlePage);
